import classnames from 'classnames';
import React, { useRef } from 'react';
import styles from './styles.module.css';
import type { ScrollableRowProps } from './types';
import { useScrollState } from './hooks/useScrollState';
import { useScroll } from './hooks/useScroll';
import { ScrollArrow } from './components/ScrollArrow';

const DEFAULT_CONFIG = {
  behavior: 'smooth',
  multiplier: 0.8
} as const;

const ScrollableRow = React.forwardRef<HTMLDivElement, ScrollableRowProps>(
  (props, ref) => {
    const {
      children,
      testId = 'scrollable-row',
      className = '',
      showArrows = true,
      scrollConfig = {},
      regressionTag
    } = props;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const config = { ...DEFAULT_CONFIG, ...scrollConfig } as const;

    const {
      left: showLeftArrow,
      right: showRightArrow,
      checkScroll
    } = useScrollState({ containerRef });

    const scroll = useScroll({ containerRef, config });

    if (!children) return null;

    return (
      <div
        className={classnames(styles.scrollableContainer, {
          [styles.showLeftFade]: showLeftArrow,
          [styles.showRightFade]: showRightArrow,
          [styles.noArrows]: !showArrows
        })}
        role="region"
        aria-label="Scrollable content"
        data-regression-tag={regressionTag}>

				{showArrows &&
        <>
						<ScrollArrow
            direction="left"
            isVisible={showLeftArrow}
            onClick={() => scroll('left')} />

						<ScrollArrow
            direction="right"
            isVisible={showRightArrow}
            onClick={() => scroll('right')} />

					</>
        }

				<div
          ref={(node) => {
            if (ref) {
              if (typeof ref === 'function') ref(node);else
              ref.current = node;
            }
            containerRef.current = node;
          }}
          className={classnames(styles.scrollableRow, className)}
          data-testid={testId}
          onScroll={checkScroll}
          role="list">

					{children}
				</div>
			</div>);

  }
);

ScrollableRow.displayName = 'ScrollableRow';

export default ScrollableRow;